"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.getDb = getDb;
exports.getStorageItem = void 0;
exports.isStorageAvailable = isStorageAvailable;
exports.setStorageItem = exports.removeStorageItem = void 0;
var _idb = require("idb");
var _logger = require("../logger");
var _getUserCheck = require("./getUserCheck");
var _isValidStorageTimestamp = require("./isValidStorageTimestamp");
const childLogger = _logger.mainLogger.createChild('IndexedDb storage');

/** Returns true if the IndexedDb storage is available. */
function isStorageAvailable() {
  const isAvailable = Boolean(typeof window === 'object' && window.indexedDB);
  if (!isAvailable) {
    (0, _logger.logWarning)('Storage is not available.');
  }
  return isAvailable;
}

/**
 * Shopping context store name. Store is basically equivalent of DB table.
 * This is currently also being used for database name.
 */
const SHOPPING_CONTEXT = 'shoppingContext';

/**
 * This is used for versioning of the DB structure.
 * When there are changes requiring different code and/or data updates,
 * the number should be incremented and optional upgrade routines for the DB structure
 * defined in the openDB() method, upgrade() subroutine below.
 */
const CURRENT_VERSION = 1;

/** IndexedDb access / open request object. */
let db;
async function getDb() {
  if (!db) {
    db = (0, _idb.openDB)(SHOPPING_CONTEXT, CURRENT_VERSION, {
      upgrade(database) {
        // create the shopping context store if missing
        if (!database.objectStoreNames.contains(SHOPPING_CONTEXT)) {
          database.createObjectStore(SHOPPING_CONTEXT);
        }
      }
    });
  }
  return db;
}

/** Returns a value identified by the key stored in IndexedDb storage. */
const getStorageItem = async key => {
  try {
    const database = await getDb();
    const data = await database.get(SHOPPING_CONTEXT, key);
    if (!data) {
      (0, _logger.logDebug)(`Item with key '${key}' wasn't found in storage.`);
      return undefined;
    }
    const {
      savedTimestamp,
      value,
      userCheck
    } = data;
    if (!(0, _isValidStorageTimestamp.isValidStorageTimestamp)(savedTimestamp)) {
      await database.delete(SHOPPING_CONTEXT, key);
      (0, _logger.logDebug)(`Stored item with key '${key}' has expired and was deleted.`);
      return undefined;
    }
    if (userCheck !== (0, _getUserCheck.getUserCheck)()) {
      await database.delete(SHOPPING_CONTEXT, key);
      (0, _logger.logDebug)(`Item with key '${key}' was stored for different user and was deleted.`);
      return undefined;
    }
    (0, _logger.logDebug)(`Item with key ${key} was found in storage.\n\t`, data);
    return value;
  } catch (error) {
    childLogger.error(error, {
      action: `getStorageItem - storing item with ${key}`
    });
    return undefined;
  }
};

/** Stores a value under the specified key in IndexedDb storage. */
exports.getStorageItem = getStorageItem;
const setStorageItem = async (key, value) => {
  try {
    const data = {
      savedTimestamp: Date.now(),
      value,
      userCheck: (0, _getUserCheck.getUserCheck)()
    };
    const database = await getDb();
    await database.put(SHOPPING_CONTEXT, data, key);
    (0, _logger.logDebug)(`Stored item with key '${key}', value:\n\t`, data);
    return true;
  } catch (error) {
    childLogger.error(error, {
      action: `setStorageItem - storing item with ${key}`
    });
    return false;
  }
};

/** Removes a value identified by the specified key from IndexedDb storage. */
exports.setStorageItem = setStorageItem;
const removeStorageItem = async key => {
  try {
    const database = await getDb();
    await database.delete(SHOPPING_CONTEXT, key);
    (0, _logger.logDebug)(`Stored item with key '${key}' was removed.`);
    return true;
  } catch (error) {
    childLogger.error(error, {
      action: `removeStorageItem - removing item with ${key}`
    });
    return false;
  }
};
exports.removeStorageItem = removeStorageItem;
